import React, { useEffect, useRef, useState } from 'react'
import '../core/scss/base.scss' // Imports fonts and icons
import '../index.css' // Tailwind gubbins
import Slider from 'react-slick'
import Button from '../core/components/atoms/Button'
import Slide from '../components/Slide'
import iconHome from '../images/home.svg'
import iconArrowRight from '../images/arrow-right.svg'
import iconArrowLeft from '../images/arrow-left.svg'
import Heading from '../core/components/atoms/Heading'
import imageIntro from '../images/intro-placeholder.jpg'
import imageCapture from '../images/placeholder1.jpg'
import imageTransport from '../images/placeholder1.jpg'
import imageStorage from '../images/placeholder1.jpg'
import imageMonitor from '../images/placeholder1.jpg'
import animationIntro from '../animations/Project-asset-homescreen-animation-2025-v4.json'
import animationCapture from '../animations/capture-animation.json'
import animationTransport from '../animations/transport-animation.json'
import animationStorage from '../animations/Shell-CCS-Storage-file-2025-v5.json'
import animationMonitor from '../animations/Shell-CCS-Monitor-file-2025-v3.json'

function HowDoesCCSWork() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const allSlides = [
    {
      title: '',
      icon: iconHome,
      image: imageIntro,
      lottieSettings: {
        autoplay: true,
        animationData: animationIntro,
        useDotLottie: true
      },
      content: (
        <div>
          <p className='text-lg lg:text-xl text-text mb-4'>
            Carbon capture and storage (CCS) is a combination of technologies that capture, transport and store carbon
            dioxide (CO<sub>2</sub>) deep underground, preventing its release into the atmosphere.
          </p>
          <p className='text-md lg:text-lg text-text mb-4 font-shell-medium font-medium'>
            CCS is one of the solutions that can be used to reduce emissions from sectors that are slower-to-decarbonise
            such as the production of steel, cement, or chemicals or to help decarbonise power stations that use fuels
            like oil and gas. These are all areas where there are few scalable, low-carbon alternatives available today.
          </p>
          <p className='text-md lg:text-lg text-text mb-4 font-shell-medium font-medium'>
            CCS can also help enable the creation of low carbon energy products such as biofuels and hydrogen, and lower
            carbon intensity hydrocarbons like liquefied natural gas (LNG).
          </p>
          <Button
            text='Explore the process'
            icon={iconArrowRight}
            tabIndex={currentIndex == 0 ? 0 : -1}
            onClick={() => jumpToSlide(1)}
          />
        </div>
      )
    },
    {
      title: 'Capture',
      image: imageCapture,
      lottieSettings: {
        animationData: animationCapture
      },
      content: (
        <div>
          <p className='text-text text-lg lg:mb-8  font-shell-medium font-medium'>
            The CO<sub>2</sub> being released needs to be captured for storage. Once captured, the CO<sub>2</sub> is
            separated from other gases, usually using a chemical solvent. The captured CO<sub>2</sub> is then separated
            from the solvent, compressed, and made ready for transport.
          </p>
        </div>
      )
    },
    {
      title: 'Transport',
      image: imageTransport,
      lottieSettings: {
        animationData: animationTransport
      },
      content: (
        <div>
          <p className='text-text text-lg lg:mb-8 font-shell-medium font-medium'>
            Transport can happen in several ways. The captured CO<sub>2</sub> is generally pumped through a pipeline to
            a storage site, which may be onshore or offshore. The CO<sub>2</sub> can also be moved by ship, rail or
            truck.
          </p>
        </div>
      )
    },
    {
      title: 'Storage',
      image: imageStorage,
      lottieSettings: {
        animationData: animationStorage
      },
      content: (
        <div>
          <p className='text-text text-lg lg:mb-8 font-shell-medium font-medium'>
            Once the CO<sub>2</sub> arrives at the storage site, it is injected deep underground into the microscopic
            spaces in porous rocks. Impermeable rock, which is called cap rock and sits above the porous rock, forms a
            seal that ensures the CO<sub>2</sub> remains deep underground.
          </p>
        </div>
      )
    },
    {
      title: 'Monitor',
      image: imageMonitor,
      lottieSettings: {
        animationData: animationMonitor
      },
      content: (
        <div>
          <p className='text-text text-lg lg:mb-8 font-shell-medium font-medium'>
            To make sure that the CO<sub>2</sub> remains permanently underground, regular monitoring takes place above
            and below ground to confirm the CO<sub>2</sub> is safely stored.
          </p>
        </div>
      )
    }
  ]

  const [slides, setSlides] = useState(allSlides)

  let sliderRef = useRef(null)
  let lottieRefs = useRef([])

  const slickSettings = {
    arrows: false,
    accessibility: false,
    dots: false,
    fade: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    waitForAnimate: false,
    draggable: false
  }

  const nextSlide = () => {
    if (currentIndex < slides.length - 1) {
      const newIndex = currentIndex + 1
      setCurrentIndex(newIndex)
    }
  }

  const previousSlide = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1
      setCurrentIndex(newIndex)
    }
  }

  const jumpToSlide = (index) => {
    setCurrentIndex(index)
  }

  useEffect(() => {
    // Check it's a number
    if (typeof currentIndex === 'number') {
      sliderRef.slickGoTo(currentIndex)
    }
  }, [currentIndex])

  return (
    <div className='asset py-8 px-4 lg:px-0 lg:py-14 mx-auto' data-theme='primary'>
      <h2 className='text-center text-4xl text-text font-shell-heavy'>How does CCS work?</h2>
      <div className=' bg-white shadow-lg mt-14 lg:mt-16 rounded-lg relative pt-8 lg:pt-14'>
        <nav className='absolute -top-10 w-full'>
          <div className='flex space-around w-full px-4'>
            {/* Home button on desktop */}
            <Button
              key={'home'}
              variant='icon'
              ariaLabel='Home'
              classes='px-4'
              icon={iconHome}
              isSelected={currentIndex == 0}
              // classes={`${currentIndex == 0 ? '' : 'hidden md:inline-flex'}`}
              onClick={() => jumpToSlide(0)}
            />
            <Button
              variant='icon'
              classes='ml-auto lg:ml-10 px-4'
              ariaLabel='Previous slide'
              icon={iconArrowLeft}
              isDisabled={currentIndex == 0}
              onClick={previousSlide}
              theme='secondaryreversed'
              currentSlide={allSlides[currentIndex].title}
            />
            {allSlides.map((slide, index) => {
              if (slide.icon) {
                // return (
                //   // Home button on mobile
                //   <Button
                //     key={index}
                //     text='Introduction'
                //     isSelected={index == currentIndex}
                //     classes={`md:hidden ${index == currentIndex ? 'inline-flex' : 'hidden'}`}
                //     onClick={() => jumpToSlide(index)}
                //   />
                // )
              } else {
                return (
                  // Other buttons
                  <Button
                    key={index}
                    text={slide.title}
                    isSelected={index === currentIndex}
                    //classes={`${index == currentIndex ? '' : 'hidden md:inline-flex flex-grow items-center'}`}
                    classes='hidden md:flex flex-grow justify-around'
                    onClick={() => jumpToSlide(index)}
                  />
                )
              }
            })}
            <Button
              variant='icon'
              classes='px-4 lg:mr-28'
              icon={iconArrowRight}
              ariaLabel='Next slide'
              isDisabled={currentIndex == slides.length - 1}
              onClick={nextSlide}
              theme='secondaryreversed'
              currentSlide={allSlides[currentIndex].title == '' ? 'Introduction' : allSlides[currentIndex].title}
            />
          </div>
        </nav>
        <Slider
          ref={(slider) => {
            sliderRef = slider
          }}
          beforeChange={(oldIndex, newIndex) => {
            //Stop the animation before we slide, hide it essentially

            if (oldIndex !== newIndex) {
              lottieRefs.current[newIndex].stop()
            }
            setCurrentIndex(newIndex)
          }}
          afterChange={(index) => {
            //Play the animation once the slide is in view
            lottieRefs.current[index].play()
          }}
          {...slickSettings}
        >
          {/* loop through allSlides */}
          {allSlides.map((slide, index) => {
            return (
              <div key={index}>
                <Slide
                  title={slide.title}
                  image={slide.image}
                  lottieSettings={slide.lottieSettings}
                  lottieRef={(el) => {
                    lottieRefs.current[index] = el
                  }}
                >
                  {slide.content}
                </Slide>
              </div>
            )
          })}
        </Slider>
      </div>
    </div>
  )
}

export default HowDoesCCSWork
